import classNames from "classnames";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useAuthedRequest } from "shared/@auth/Auth";
import { useLogger } from "shared/@hooks/useLogger";
import { useQueryParams } from "shared/@hooks/useQueryParams";
import { RequestCache } from "shared/@hooks/useRequestCache";
import Link from "shared/Link";
import Loader from "shared/Loader";
import PinLabel from "shared/PinLabel";
import Table from "shared/Table";
import { TableSortValue } from "shared/TableSortHeader";
import NameBubble from "../NameBubble";
import EndArrow from "./EndArrow";
import styles from "./index.module.css";
import NestingMarkers from "./NestingMarkers";
import { DownlineCache } from "./useDownlineCache";
import TableFilterSortHeader from "../TableFilterSortHeader";
import {
  applyTableFilters,
  defaultStatusFilter,
  membershipOf,
  parseCsvFilter,
  pinOf,
  sortRows,
  sortToHeaderValue,
  statusOf,
  toCsvFilter,
  toOptions,
} from "../tableUtils";

/**
 * Explore an IBO's downline leg-by-leg.
 */
export default function DownlineTree({ iboNumber, debug = false }) {
  const { logError } = useLogger({ prefix: "DownlineTree" });
  const query = useQueryParams();
  const q = query.get();

  // Clear all downline filter/sort params when iboNumber changes or component unmounts,
  // so filters do not carry over when navigating to a different IBO.
  useEffect(() => {
    return () => {
      query.set({
        "los-downline-sort": null,
        "los-downline-status": null,
        "los-downline-membership": null,
        "los-downline-pin": null,
      });
    };
  }, [iboNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const sort = q["los-downline-sort"] ?? null;
  const statusFilters = parseCsvFilter(
    q["los-downline-status"],
    defaultStatusFilter
  );
  const membershipFilters = parseCsvFilter(q["los-downline-membership"]);
  const pinFilters = parseCsvFilter(q["los-downline-pin"]);

  const hasExplicitPinFilter = Object.prototype.hasOwnProperty.call(
    q,
    "los-downline-pin"
  );
  const hasExplicitMembershipFilter = Object.prototype.hasOwnProperty.call(
    q,
    "los-downline-membership"
  );
  // Status defaults to current and is treated as an explicit filter by design.
  const hasExplicitStatusFilter = true;

  const isModified =
    Object.prototype.hasOwnProperty.call(q, "los-downline-sort") ||
    Object.prototype.hasOwnProperty.call(q, "los-downline-status") ||
    Object.prototype.hasOwnProperty.call(q, "los-downline-membership") ||
    Object.prototype.hasOwnProperty.call(q, "los-downline-pin");

  const resetFilters = () =>
    query.set({
      "los-downline-sort": null,
      "los-downline-status": null,
      "los-downline-membership": null,
      "los-downline-pin": null,
    });

  const activeFilters = useMemo(
    () => ({
      statuses: statusFilters,
      memberships: membershipFilters,
      pins: pinFilters,
    }),
    [statusFilters, membershipFilters, pinFilters]
  );

  const cache = RequestCache.useContainer();
  const { data, loading, error } = useAuthedRequest(
    `/v1/membership/los/head/${iboNumber}`,
    null,
    cache
  );

  if (error) {
    logError(error);
  }

  const [knownNodes, setKnownNodes] = useState({});
  const [dropdownHeights, setDropdownHeights] = useState({});

  useEffect(() => {
    if (data != null) {
      setKnownNodes((prev) => ({
        ...prev,
        [data.iboNumber]: data,
      }));
    }
  }, [data]);

  const onNodesSeen = useCallback((nodes) => {
    if (!nodes || nodes.length === 0) {
      return;
    }

    setKnownNodes((prev) => {
      const next = { ...prev };

      nodes.forEach((node) => {
        if (node?.iboNumber != null) {
          next[node.iboNumber] = node;
        }
      });

      return next;
    });
  }, []);

  const knownNodeList = useMemo(() => Object.values(knownNodes), [knownNodes]);

  const statusOptions = useMemo(() => {
    return toOptions(
      knownNodeList.map((item) => statusOf(item)),
      ["current"]
    );
  }, [knownNodeList]);

  const membershipOptions = useMemo(() => {
    return toOptions(knownNodeList.map((item) => membershipOf(item)));
  }, [knownNodeList]);

  const pinOptions = useMemo(() => {
    return toOptions(knownNodeList.map((item) => pinOf(item)));
  }, [knownNodeList]);

  const displayedNodes = useMemo(() => {
    return applyTableFilters(knownNodeList, {
      statuses: statusFilters,
      memberships: membershipFilters,
      pins: pinFilters,
    });
  }, [knownNodeList, statusFilters, membershipFilters, pinFilters]);

  const implicitPinCheckedValues = useMemo(() => {
    return [...new Set(displayedNodes.map((item) => pinOf(item)))];
  }, [displayedNodes]);

  const implicitStatusCheckedValues = useMemo(() => {
    return [...new Set(displayedNodes.map((item) => statusOf(item)))];
  }, [displayedNodes]);

  const implicitMembershipCheckedValues = useMemo(() => {
    return [...new Set(displayedNodes.map((item) => membershipOf(item)))];
  }, [displayedNodes]);

  const onDropdownMenuStateChange = useCallback((key, isOpen, height) => {
    setDropdownHeights((prev) => {
      if (!isOpen || !height) {
        if (prev[key] == null) {
          return prev;
        }

        const next = { ...prev };
        delete next[key];
        return next;
      }

      if (prev[key] === height) {
        return prev;
      }

      return {
        ...prev,
        [key]: height,
      };
    });
  }, []);

  const dropdownBottomSpace = useMemo(() => {
    const heights = Object.values(dropdownHeights);
    const maxHeight = heights.length > 0 ? Math.max(...heights) : 0;
    return maxHeight > 0 ? maxHeight + 12 : 0;
  }, [dropdownHeights]);

  return loading || data == null ? (
    <Loader />
  ) : (
    <div
      className={styles.componentContainer}
      style={
        dropdownBottomSpace > 0
          ? { paddingBottom: `${dropdownBottomSpace}px` }
          : undefined
      }
    >
      <Table
        allowContentOverflow={true}
        className={classNames({
          [styles.debug]: debug, // display marker guides if true
        })}
      >
        <thead>
          <tr>
            <th>
              <div className={styles.nameHeader}>
                Name
                {isModified && (
                  <button className={styles.resetButton} onClick={resetFilters}>
                    Reset LOS
                  </button>
                )}
              </div>
            </th>
            <th>IBO Number</th>
            <th>
              <TableFilterSortHeader
                title="Pin Level"
                options={pinOptions}
                selectedValues={pinFilters}
                implicitCheckedValues={implicitPinCheckedValues}
                isExplicitSelection={hasExplicitPinFilter}
                onMenuStateChange={(isOpen, height) =>
                  onDropdownMenuStateChange("pin", isOpen, height)
                }
                onFilterChange={(next) => {
                  query.set({ "los-downline-pin": toCsvFilter(next) });
                }}
                sortValue={sortToHeaderValue(sort, "pin")}
                onSortChange={(value) => {
                  if (value === TableSortValue.ascending) {
                    query.set({ "los-downline-sort": "pin-asc" });
                  } else {
                    query.set({ "los-downline-sort": "pin-desc" });
                  }
                }}
              />
            </th>
            <th>
              <TableFilterSortHeader
                title="Status"
                options={statusOptions}
                selectedValues={statusFilters}
                implicitCheckedValues={implicitStatusCheckedValues}
                isExplicitSelection={hasExplicitStatusFilter}
                onMenuStateChange={(isOpen, height) =>
                  onDropdownMenuStateChange("status", isOpen, height)
                }
                onFilterChange={(next) => {
                  query.set({ "los-downline-status": toCsvFilter(next) });
                }}
                sortValue={sortToHeaderValue(sort, "status")}
                onSortChange={(value) => {
                  if (value === TableSortValue.ascending) {
                    query.set({ "los-downline-sort": "status-asc" });
                  } else {
                    query.set({ "los-downline-sort": "status-desc" });
                  }
                }}
              />
            </th>
            <th>
              <TableFilterSortHeader
                title="Membership"
                options={membershipOptions}
                selectedValues={membershipFilters}
                implicitCheckedValues={implicitMembershipCheckedValues}
                isExplicitSelection={hasExplicitMembershipFilter}
                onMenuStateChange={(isOpen, height) =>
                  onDropdownMenuStateChange("membership", isOpen, height)
                }
                onFilterChange={(next) => {
                  query.set({ "los-downline-membership": toCsvFilter(next) });
                }}
                sortValue={sortToHeaderValue(sort, "membership")}
                onSortChange={(value) => {
                  if (value === TableSortValue.ascending) {
                    query.set({ "los-downline-sort": "membership-asc" });
                  } else {
                    query.set({ "los-downline-sort": "membership-desc" });
                  }
                }}
              />
            </th>
          </tr>
        </thead>
        <tbody className={styles.tableBody}>
          <TreeEntry
            iboInfo={data}
            autoLoad={true}
            activeFilters={activeFilters}
            sort={sort}
            onNodesSeen={onNodesSeen}
            isRoot={true}
          />
        </tbody>
      </Table>
    </div>
  );
}

/**
 * TreeEntry represents one IBO. Each TreeEntry renders itself and
 * more TreeEntries below it for downline when expanded.
 */
const TreeEntry = memo(function TreeEntry({
  iboInfo,
  autoLoad,
  nesting = [],
  activeFilters,
  sort,
  onNodesSeen,
  isRoot = false,
}) {
  const getFrontline = DownlineCache.useContainer();

  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [frontline, setFrontline] = useState(null);

  const {
    iboNumber,
    pinLevel,
    status,
    membership,
    hasDigitalDelivery,
    hasDreamStream,
  } = iboInfo;

  useEffect(() => {
    onNodesSeen([iboInfo]);
  }, [iboInfo, onNodesSeen]);

  // Load frontline when first expanded.
  useEffect(() => {
    if (expanded && frontline == null) {
      setLoading(true);
      getFrontline(iboNumber)
        .then((results) => {
          setFrontline(results);
          onNodesSeen(results);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [expanded, frontline, getFrontline, iboNumber, onNodesSeen]);

  // Auto-expand to trigger loading if 'autoLoad' is true.
  useEffect(() => {
    if (autoLoad) {
      setExpanded(true);
    }
  }, [autoLoad]);

  const expandedWithFrontline =
    expanded && frontline != null && frontline.length > 0;

  const filteredFrontline = useMemo(() => {
    const filtered = applyTableFilters(frontline ?? [], activeFilters);
    return sortRows(filtered, sort);
  }, [frontline, activeFilters, sort]);

  const isVisible =
    isRoot || applyTableFilters([iboInfo], activeFilters).length > 0;

  const onExpandToggle = useCallback(
    (e) => {
      setExpanded(!expanded);
    },
    [expanded]
  );

  return (
    <>
      {isVisible && (
        <tr key={iboNumber} className={styles.row}>
          <td>
            <div className={styles.nameContainer}>
              <NestingMarkers
                nesting={nesting}
                expanded={expandedWithFrontline}
                onExpandToggle={onExpandToggle}
              />

              <div
                className={classNames(styles.nameBubble, {
                  [styles.loading]: loading,
                  [styles.noDownline]:
                    frontline != null && frontline.length === 0,
                })}
              >
                <div className={styles.bubbleFade}>
                  <NameBubble
                    {...iboInfo}
                    onClick={(e) => {
                      e.preventDefault();
                      setExpanded(!expanded);
                    }}
                  />
                </div>

                {loading && (
                  <div className={styles.bubbleLoader}>
                    <Loader />
                  </div>
                )}

                {expandedWithFrontline && (
                  <div className={styles.firstChildConnector} />
                )}
              </div>

              <EndArrow />
            </div>
          </td>

          <td>
            <Link to={`/membership/lookup/ibo/${iboNumber}`}>{iboNumber}</Link>
          </td>

          <td>
            <PinLabel pinLevel={pinLevel} />
          </td>

          <td className={styles.capitalize}>
            {status ? (
              <span>{status.toLowerCase()}</span>
            ) : (
              <span className={styles.faded}>None</span>
            )}
          </td>

          <td className={styles.capitalize}>
            {membership ? (
              <>
                <span>{membership.toLowerCase()}</span>
                {hasDigitalDelivery && hasDreamStream ? (
                  <span> +</span>
                ) : (
                  <>
                    {hasDigitalDelivery && <span> + DD</span>}
                    {hasDreamStream && <span> + Topics</span>}
                  </>
                )}
              </>
            ) : (
              <span className={styles.faded}>No Membership</span>
            )}
          </td>
        </tr>
      )}

      {expandedWithFrontline &&
        filteredFrontline.map((info, i) => {
          const isLastChild = i + 1 === filteredFrontline.length;

          // Clone nesting to mutate.
          const nextNesting = nesting.map((x) => x);

          // Replace self with descending marker (conveniently 'self - 1').
          let self = nextNesting.pop();
          if (self) {
            nextNesting.push(self - 1);
          }

          // Assign child marker based on list position.
          nextNesting.push(isLastChild ? 1 : 3);

          return (
            <TreeEntry
              key={info.iboNumber}
              iboInfo={info}
              autoLoad={false}
              nesting={nextNesting}
              activeFilters={activeFilters}
              sort={sort}
              onNodesSeen={onNodesSeen}
            />
          );
        })}
    </>
  );
});
